import axios from 'axios';

declare global {
  interface Window {
    __ASM_CONFIG__?: {
      API_URL?: string;
    };
  }
}

/** API-URL: Runtime (config.js / Server-.env) vor Build-Zeit (REACT_APP_API_URL). */
export const resolveApiBaseUrl = (): string => {
  const runtime = typeof window !== 'undefined'
    ? window.__ASM_CONFIG__?.API_URL?.trim()
    : undefined;
  const fromEnv = process.env.REACT_APP_API_URL?.trim();
  const raw = runtime || fromEnv || 'http://localhost:3001';
  return raw.replace(/\/$/, '');
};

// Create axios instance (baseURL wird pro Request gesetzt)
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token + runtime API base URL
api.interceptors.request.use(
  (config: any) => {
    config.baseURL = resolveApiBaseUrl();
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 [API] Request with token:', token.substring(0, 20) + '...');
    } else {
      console.log('⚠️ [API] Request without token');
    }
    return config;
  },
  (error: any) => {
    console.error('❌ [API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => {
    console.log('✅ [API] Response success:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    console.log('🔍 [API] Response error:', {
      status: error.response?.status,
      url: error.config?.url,
      retry: originalRequest._retry
    });
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔄 [API] 401 Error detected, attempting token refresh...');
      originalRequest._retry = true;
      
      // Try to refresh the token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        console.log('🔄 [API] Refresh token found, attempting refresh...');
        try {
          const response = await axios.post(`${resolveApiBaseUrl()}/api/auth/refresh`, {
            refreshToken: refreshToken
          });
          
          console.log('✅ [API] Token refresh successful');
          
          if (response.data.accessToken) {
            const oldToken = localStorage.getItem('accessToken');
            localStorage.setItem('accessToken', response.data.accessToken);
            console.log('🔄 [API] New access token stored:', response.data.accessToken.substring(0, 20) + '...');
            
            if (response.data.refreshToken) {
              localStorage.setItem('refreshToken', response.data.refreshToken);
              console.log('🔄 [API] New refresh token stored');
            }
            
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
            console.log('🔄 [API] Retrying original request with new token...');
            return api(originalRequest);
          }
        } catch (refreshError) {
          const errorMessage = axios.isAxiosError(refreshError)
            ? `${refreshError.response?.status} - ${refreshError.message}`
            : 'Unknown error';
          console.error('❌ [API] Token refresh failed:', errorMessage);
          console.log('🚪 [API] Forcing logout due to refresh failure');
          
          // Refresh failed, logout user
          if ((window as any).forceLogout) {
            (window as any).forceLogout();
          } else {
            // Fallback if forceLogout is not available
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('tempAccessToken');
            localStorage.removeItem('tempRefreshToken');
            localStorage.removeItem('organizations');
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } else {
        console.log('❌ [API] No refresh token available, forcing logout');
        // No refresh token available, logout user
        if ((window as any).forceLogout) {
          (window as any).forceLogout();
        } else {
          // Fallback if forceLogout is not available
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('tempAccessToken');
          localStorage.removeItem('tempRefreshToken');
          localStorage.removeItem('organizations');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    
    console.log('❌ [API] Non-401 error or already retried, rejecting:', error.message);
    return Promise.reject(error);
  }
);

export default api; 